import React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import * as styles from './style'
import Player from 'video.js/dist/types/player'

interface Props {
  videoJsPlayerOptions: any
  videoAsset?: {
    playbackUrlDash?: string
    playbackUrlHLS?: string
    playbackUrlMP4?: string
    videoPoster?: string
  }
  onPlayerReady?: (player: Player) => void
}

const VideoJSPlayer: React.FC<Props> = ({ videoAsset, videoJsPlayerOptions, onPlayerReady }): JSX.Element => {
  const videoElementRef = useRef<HTMLVideoElement>(null)
  const videoJsPlayerRef = useRef(false)
  const [videoJsPlayer, setVideoJsPlayer] = useState<Player>()

  const videoSources = useMemo(() => {
    const sources: { src: string; type: string }[] = []
    if (videoAsset?.playbackUrlHLS) sources.push({ src: videoAsset.playbackUrlHLS, type: 'application/x-mpegURL' })
    if (videoAsset?.playbackUrlMP4) sources.push({ src: videoAsset.playbackUrlMP4, type: 'video/mp4' })
    if (videoAsset?.playbackUrlDash) sources.push({ src: videoAsset.playbackUrlDash, type: 'application/dash+xml' })
    return sources
  }, [videoAsset?.playbackUrlDash, videoAsset?.playbackUrlHLS, videoAsset?.playbackUrlMP4])

  useEffect(() => {
    if (!videoElementRef.current) return
    const player = videojs(
      videoElementRef.current,
      {
        ...videoJsPlayerOptions,
        html5: {
          vhs: {
            overrideNative: true,
          },
        },
        sources: videoSources,
        poster: videoAsset?.videoPoster,
      },
      () => {
        videojs.log('videojs.player ready')
        setVideoJsPlayer(player)
        if (onPlayerReady) {
          onPlayerReady(player)
        }
      },
    )

    return () => {
      videoJsPlayerRef.current = true
      if (videoJsPlayer && !videoJsPlayer.isDisposed()) {
        videoJsPlayer.dispose()
      }
    }
  }, [])

  return (
    <video
      ref={videoElementRef}
      className={`video-js vjs-default-skin ${styles.video}`}
      playsInline
      data-vjs-player
    ></video>
  )
}

export default VideoJSPlayer
